import { useEffect, useState } from "react";
import { AlertTriangle, Pencil, RotateCcw } from "lucide-react";
import { api, inr, monthLabel, formatApiError } from "../lib/api";

const numCls = "w-20 sm:w-24 bg-[#0F0F0F] border border-white/15 text-white text-sm px-2 py-1.5 rounded-sm text-right focus:outline-none focus:border-[#D4AF37]";
const keyOf = (r) => `${r.month}|${r.category}`;

// Live preview of how a single amount is split. `manual` = null (auto) or { "month|category": amount }.
export const AllocationPreview = ({ playerId, date, amount, useCredit, setUseCredit, manual, setManual, excludeId, onPreview }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!playerId || !date) { setData(null); onPreview(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const body = { player_id: playerId, date, amount: Number(amount) || 0, use_credit: useCredit,
          allocations: manual ? Object.entries(manual).map(([k, v]) => { const [month, category] = k.split("|"); return { month, category, amount: Number(v) || 0 }; }) : null };
        const { data: d } = await api.post("/admin/payments/allocation-preview", body, { params: excludeId ? { exclude_payment_id: excludeId } : {} });
        setData(d); setError(""); onPreview(d);
      } catch (err) { setError(formatApiError(err)); onPreview(null); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, date, amount, useCredit, manual, excludeId]);

  if (!playerId) return <p className="text-white/30 text-xs mt-5" data-testid="alloc-empty">Pick a player to see how the payment will be allocated.</p>;
  if (!data && !error) return <p className="text-white/30 text-xs mt-5">Working out allocation…</p>;

  const startManual = () => setManual(Object.fromEntries((data?.rows || []).map((r) => [keyOf(r), String(r.allocated || 0)])));

  return (
    <div className="mt-6 border border-white/10 bg-[#050505]" data-testid="alloc-preview">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Allocation Preview {manual && <span className="text-white/40 ml-2">· manual</span>}</p>
        {manual ? (
          <button type="button" onClick={() => setManual(null)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/60 hover:text-[#D4AF37]" data-testid="alloc-auto"><RotateCcw size={12} /> Back to automatic</button>
        ) : (
          <button type="button" onClick={startManual} disabled={!data?.rows?.length} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/60 hover:text-[#D4AF37] disabled:opacity-30" data-testid="alloc-edit"><Pencil size={12} /> Edit allocation</button>
        )}
      </div>
      {data && data.credit_available > 0 && (
        <label className="flex items-center gap-2 px-4 py-3 border-b border-white/10 text-sm text-white/80 cursor-pointer" data-testid="alloc-credit-toggle">
          <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} className="accent-[#D4AF37]" data-testid="alloc-use-credit" />
          Apply available credit of <span className="text-green-300 font-semibold">{inr(data.credit_available)}</span>
        </label>
      )}
      {data && data.rows.length === 0 && <p className="text-white/40 text-sm p-4" data-testid="alloc-no-dues">No outstanding dues — the full amount will be kept as credit.</p>}
      {data && data.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
                <th className="px-2 sm:px-4 py-2">Fee Category</th><th className="px-2 sm:px-4 py-2 text-right">Outstanding</th><th className="px-2 sm:px-4 py-2 text-right">Allocated</th><th className="px-2 sm:px-4 py-2 text-right">Remaining</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={keyOf(r)} className="border-b border-white/5" data-testid={`alloc-row-${r.category}-${r.month}`}>
                  <td className="px-2 sm:px-4 py-2">{r.label}<span className="text-white/40 text-[11px] block sm:inline sm:ml-1">{monthLabel(r.month)}</span></td>
                  <td className="px-2 sm:px-4 py-2 text-right text-white/60">{inr(r.outstanding)}</td>
                  <td className="px-2 sm:px-4 py-2 text-right">
                    {manual ? (
                      <input type="number" min="0" max={r.outstanding} value={manual[keyOf(r)] ?? ""} onChange={(e) => setManual({ ...manual, [keyOf(r)]: e.target.value })} className={numCls} data-testid={`alloc-input-${r.category}-${r.month}`} />
                    ) : <span className={r.allocated > 0 ? "text-[#D4AF37] font-semibold" : "text-white/30"}>{inr(r.allocated)}</span>}
                  </td>
                  <td className="px-2 sm:px-4 py-2 text-right text-white/60">{inr(r.remaining)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {error && <p className="flex items-center gap-2 text-red-400 text-xs px-4 py-3 border-t border-white/10" data-testid="alloc-error"><AlertTriangle size={13} /> {error}</p>}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border-t border-white/10 text-xs" data-testid="alloc-summary">
          {[["Allocated", data.total_allocated, "text-[#D4AF37]", "alloc-total"], ["From Credit", data.credit_used, "text-green-300", "alloc-credit-used"],
            ["Kept as Credit", data.excess_credit, "text-green-300", "alloc-excess"], ["Balance After", data.balance_after, "text-white", "alloc-balance-after"]].map(([l, v, c, id]) => (
            <div key={l} className="bg-[#0A0A0A] px-4 py-3" data-testid={id}><p className="text-white/40 uppercase tracking-widest text-[10px]">{l}</p><p className={`font-semibold text-base mt-1 ${c}`}>{inr(v)}</p></div>
          ))}
        </div>
      )}
      {data?.warning && <p className="flex items-start gap-2 text-amber-300 text-xs px-4 py-3 border-t border-white/10" data-testid="alloc-warning"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {data.warning}</p>}
      {data?.credit_history?.length > 0 && (
        <details className="border-t border-white/10 px-4 py-3 text-xs text-white/60" data-testid="alloc-credit-history">
          <summary className="cursor-pointer uppercase tracking-widest text-[10px] text-white/40">Credit history</summary>
          {data.credit_history.map((h) => (
            <p key={h.payment_id} className="mt-1.5">{h.date} · {h.amount > 0 ? `Received ${inr(h.amount)}` : `Used ${inr(-h.amount)}`} · balance {inr(h.balance)}</p>
          ))}
        </details>
      )}
    </div>
  );
};
